import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { addRecent } from '../recent';
import {
  Avatar,
  CustomFieldInput,
  ErrorBox,
  FormattedText,
  Modal,
  PriorityIcon,
  StatusBadge,
  TypeIcon,
  UserChip,
  customFieldDisplay,
  fmtDate,
  fmtMinutes,
  relTime,
  useIssueRowColor,
} from '../components';
import { MentionTextarea, type TextFormat } from '../editor';
import { FileList, PendingFiles, pastedFiles } from '../attachments';
import { afterId, useDragReorder } from '../dragReorder';
import type { HistoryRow, IssueDetail, Note, UserRef } from '../types';
import { N_, t } from '../i18n';
import { IssuePlanningFields, NeededTimeFromHistory, planningPayload, statusSpan, storyPointsText } from '../IssuePlanningFields';
import { effortInput } from '../../../shared/issuePlanning';

const pad7 = (n: number | string) => String(n).padStart(7, '0');

export function IssuePage() {
  const { id } = useParams();
  const issueId = Number(id);
  const qc = useQueryClient();
  const location = useLocation();
  const { data, error, isLoading } = useQuery({
    queryKey: ['issue', issueId],
    queryFn: () => api.get<IssueDetail>(`/api/issues/${issueId}`),
    enabled: Number.isFinite(issueId),
  });
  const [mode, setMode] = useState<'view' | 'edit' | 'reminder'>('view');
  const [statusTo, setStatusTo] = useState<number | null>(null);

  useEffect(() => {
    if (data) addRecent(data.issue.id);
  }, [data?.issue.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setMode('view');
    setStatusTo(null);
  }, [issueId]);
  // scroll to #c123 / #notes / #history once loaded
  useEffect(() => {
    if (data && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [data, location.hash]);

  const setDetail = (d: IssueDetail) => {
    qc.setQueryData(['issue', issueId], d);
    qc.invalidateQueries({ queryKey: ['issues'] });
    qc.invalidateQueries({ queryKey: ['myview'] });
  };

  if (isLoading) return <div className="subtle">{t('Loading…')}</div>;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  return (
    <div className="page issue-page">
      <DetailsCard d={data} onChange={setDetail} mode={mode} setMode={setMode} onStatus={setStatusTo} />
      {statusTo !== null && (
        <StatusChange
          d={data}
          to={statusTo}
          onDone={(nd) => {
            setStatusTo(null);
            setDetail(nd);
          }}
          onCancel={() => setStatusTo(null)}
        />
      )}
      <Relationships d={data} onChange={setDetail} />
      <Monitors d={data} onChange={setDetail} />
      <Activities d={data} onChange={setDetail} />
      <History d={data} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function Section({ id, title, icon, actions, children, collapsible = true, className }: { id?: string; title: ReactNode; icon?: ReactNode; actions?: ReactNode; children: ReactNode; collapsible?: boolean; className?: string }) {
  const [open, setOpen] = useState(true);
  return (
    <section className={`card ${className ?? ''}`} id={id}>
      <div className="card-head">
        <h2>
          {icon && <span className="head-icon">{icon}</span>}
          {title}
        </h2>
        <div className="card-actions">
          {actions}
          {collapsible && (
            <button className="icon-btn" onClick={() => setOpen(!open)} title={open ? t('Collapse') : t('Expand')} aria-expanded={open}>
              {open ? '▴' : '▾'}
            </button>
          )}
        </div>
      </div>
      {open && <div className="card-body">{children}</div>}
    </section>
  );
}

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`dfield ${wide ? 'wide' : ''}`}>
      <div className="dlabel">{label}</div>
      <div className="dvalue">{children || <span className="subtle">—</span>}</div>
    </div>
  );
}

function DetailsCard({
  d,
  onChange,
  mode,
  setMode,
  onStatus,
}: {
  d: IssueDetail;
  onChange: (d: IssueDetail) => void;
  mode: 'view' | 'edit' | 'reminder';
  setMode: (m: 'view' | 'edit' | 'reminder') => void;
  onStatus: (s: number) => void;
}) {
  const { label } = useMeta();
  const { issue, perms } = d;
  // old issues without any needed time: offer confirmed → resolved from the history right here, not only when editing
  const span = perms.update && issue.needed_time_source === 'time_entries' && !issue.needed_minutes ? statusSpan(d.history) : null;
  const saveNeeded = useMutation({
    mutationFn: (minutes: number) => api.patch<IssueDetail>(`/api/issues/${issue.id}`, { needed_minutes: minutes }),
    onSuccess: onChange,
  });
  const jump = (anchor: string) => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <section className="card details-card">
      <div className="issue-title">
        <div className="crumbs subtle small">
          {issue.project_name}
          {issue.category_name && <> / {issue.category_name}</>}
        </div>
        <h1>
          <TypeIcon severity={issue.severity} size={20} />
          <span>
            <span className="key-big">{pad7(issue.id)}</span>: {issue.summary}
          </span>
        </h1>
      </div>
      <div className="toolbar issue-toolbar">
        {perms.add_note && (
          <button className={`btn ${mode === 'reminder' ? 'selected' : ''}`} onClick={() => setMode(mode === 'reminder' ? 'view' : 'reminder')}>
            {t('Send a Reminder')}
          </button>
        )}
        <button className="btn" onClick={() => jump('notes')}>
          {t('Jump to Notes')}
        </button>
        <button className="btn" onClick={() => jump('history')}>
          {t('Jump to History')}
        </button>
      </div>

      {mode === 'reminder' && <Reminder d={d} onDone={(nd) => { setMode('view'); onChange(nd); }} onCancel={() => setMode('view')} />}

      {mode === 'edit' ? (
        <EditForm d={d} onSaved={(nd) => { setMode('view'); onChange(nd); }} onCancel={() => setMode('view')} />
      ) : (
        <>
          <div className="dgrid">
            <Field label={t('ID')}>{pad7(issue.id)}</Field>
            <Field label={t('Project')}>{issue.project_name}</Field>
            <Field label={t('Category')}>{issue.category_name}</Field>
            <Field label={t('View Status')}>
              {issue.view_state === 50 ? <span className="tag-mini private">{t('private')}</span> : t('public')}
            </Field>
            <Field label={t('Date Submitted')}>
              <span title={relTime(issue.created_at)}>{fmtDate(issue.created_at)}</span>
            </Field>
            <Field label={t('Last Update')}>
              <span title={relTime(issue.updated_at)}>{fmtDate(issue.updated_at)}</span>
            </Field>

            <Field label={t('Reporter')}>
              <UserChip name={issue.reporter_name} />
            </Field>
            <Field label={t('Assigned To')}>
              <UserChip name={issue.handler_name} />
            </Field>
            <Field label={t('Due Date')}>{fmtDate(issue.due_date, false)}</Field>
            <Field label={t('Priority')}>
              <span className="with-icon">
                <PriorityIcon priority={issue.priority} /> {label('priority', issue.priority)}
              </span>
            </Field>
            <Field label={t('Severity')}>
              <span className="with-icon">
                <TypeIcon severity={issue.severity} /> {label('severity', issue.severity)}
              </span>
            </Field>
            <Field label={t('Reproducibility')}>{label('reproducibility', issue.reproducibility)}</Field>

            <Field label={t('Status')}>
              <StatusBadge status={issue.status} />
            </Field>
            <Field label={t('Resolution')}>{label('resolution', issue.resolution)}</Field>
            <Field label={t('Duplicate of')}>{issue.duplicate_id ? <Link to={`/issues/${issue.duplicate_id}`}>{pad7(issue.duplicate_id)}</Link> : ''}</Field>

            <Field label={t('Complexity / Story points')}>{storyPointsText(issue.story_points)}</Field>
            <Field label={t('Estimated time')}>{issue.estimated_minutes == null ? '' : fmtMinutes(issue.estimated_minutes)}</Field>
            {perms.time_tracking && <Field label={t('Needed time (actual)')}>
              {issue.needed_minutes == null ? '' : <>{fmtMinutes(issue.needed_minutes)} <span className="subtle small">({issue.needed_time_source === 'manual' ? t('Manual total') : t('From time entries')})</span></>}
              {span && <div><NeededTimeFromHistory span={span} statusLabel={(s) => label('status', s)} onSave={(m) => saveNeeded.mutate(m)} saving={saveNeeded.isPending} /></div>}
              <ErrorBox error={saveNeeded.error} />
            </Field>}

            <Field label={t('Product Version')}>{issue.version}</Field>
            <Field label={t('Target Version')}>{issue.target_version}</Field>
            <Field label={t('Fixed in Version')}>{issue.fixed_in_version}</Field>
            {(issue.platform || issue.os || issue.os_build) && (
              <>
                <Field label={t('Platform')}>{issue.platform}</Field>
                <Field label={t('OS')}>{issue.os}</Field>
                <Field label={t('OS Version')}>{issue.os_build}</Field>
              </>
            )}
            {(issue.build || issue.projection !== 10 || issue.eta !== 10) && (
              <>
                <Field label={t('Product Build')}>{issue.build}</Field>
                <Field label={t('Projection')}>{label('projection', issue.projection)}</Field>
                <Field label={t('ETA')}>{label('eta', issue.eta)}</Field>
              </>
            )}
            {d.custom_fields.map((f) => (
              <Field key={f.id} label={f.name}>
                {customFieldDisplay(f, f.value ?? '')}
              </Field>
            ))}
          </div>

          <div className="text-blocks">
            <div className="tblock">
              <div className="dlabel">{t('Summary')}</div>
              <div className="summary-text">
                {pad7(issue.id)}: {issue.summary}
              </div>
            </div>
            {/* empty texts are not shown, as in MantisBT */}
            {issue.description && (
              <div className="tblock">
                <div className="dlabel">{t('Description')}</div>
                <FormattedText text={issue.description} format={issue.text_format} />
              </div>
            )}
            {issue.steps_to_reproduce && (
              <div className="tblock">
                <div className="dlabel">{t('Steps To Reproduce')}</div>
                <FormattedText text={issue.steps_to_reproduce} format={issue.text_format} />
              </div>
            )}
            {issue.additional_information && (
              <div className="tblock">
                <div className="dlabel">{t('Additional Information')}</div>
                <FormattedText text={issue.additional_information} format={issue.text_format} />
              </div>
            )}
            <div className="tblock">
              <div className="dlabel">{t('Tags')}</div>
              <Tags d={d} onChange={onChange} />
            </div>
            <IssueFiles d={d} onChange={onChange} />
          </div>
        </>
      )}

      {mode !== 'edit' && <ActionRow d={d} onChange={onChange} onEdit={() => setMode('edit')} onStatus={onStatus} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
function ActionRow({ d, onChange, onEdit, onStatus }: { d: IssueDetail; onChange: (d: IssueDetail) => void; onEdit: () => void; onStatus: (s: number) => void }) {
  const { meta, label, th } = useMeta();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { issue, perms } = d;
  const [handler, setHandler] = useState(issue.handler_id == null ? '' : String(issue.handler_id));
  const nextStatus = d.allowed_statuses.find((s) => s > issue.status) ?? d.allowed_statuses.find((s) => s !== issue.status) ?? issue.status;
  const [status, setStatus] = useState(String(nextStatus));
  const [moving, setMoving] = useState(false);
  useEffect(() => {
    setHandler(issue.handler_id == null ? '' : String(issue.handler_id));
    setStatus(String(nextStatus));
  }, [issue.handler_id, issue.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = useMutation({ mutationFn: (body: any) => api.patch<IssueDetail>(`/api/issues/${issue.id}`, body), onSuccess: onChange });
  const monitor = useMutation({
    mutationFn: () => (d.monitoring ? api.del<IssueDetail>(`/api/issues/${issue.id}/monitors/${meta.user.id}`) : api.post<IssueDetail>(`/api/issues/${issue.id}/monitors`, {})),
    onSuccess: onChange,
  });
  const del = useMutation({
    mutationFn: () => api.del(`/api/issues/${issue.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['issues'] });
      qc.invalidateQueries({ queryKey: ['myview'] });
      navigate('/issues');
    },
  });
  const canClose = issue.status >= 80 && issue.status < 90 && d.allowed_statuses.includes(90);

  return (
    <div className="action-row">
      {perms.update && (
        <button className="btn" onClick={onEdit}>
          {t('Edit')}
        </button>
      )}
      {perms.assign && (
        <span className="btn-group">
          <button className="btn" disabled={patch.isPending || handler === String(issue.handler_id ?? '')} onClick={() => patch.mutate({ handler_id: handler ? Number(handler) : null })}>
            {t('Assign To:')}
          </button>
          <select value={handler} onChange={(e) => setHandler(e.target.value)} aria-label={t('Assign to')}>
            <option value="">{t('[Nobody]')}</option>
            {d.handlers.some((u) => u.id === meta.user.id) && <option value={meta.user.id}>{t('[Myself]')}</option>}
            {issue.reporter_id !== meta.user.id && d.handlers.some((u) => u.id === issue.reporter_id) && <option value={issue.reporter_id}>{t('[Reporter]')}</option>}
            {d.handlers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.realname || u.username}
              </option>
            ))}
          </select>
        </span>
      )}
      {!perms.assign && perms.take && issue.status < 80 && (
        <button className="btn primary" onClick={() => patch.mutate({ handler_id: meta.user.id })}>
          {t('Assign to me')}
        </button>
      )}
      {perms.change_status && (
        <span className="btn-group">
          <button className="btn" disabled={status === String(issue.status)} onClick={() => onStatus(Number(status))}>
            {t('Change Status To:')}
          </button>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('New status')}>
            {d.allowed_statuses.map((s) => (
              <option key={s} value={s}>
                {label('status', s)}
              </option>
            ))}
          </select>
        </span>
      )}
      {perms.monitor && (
        <button className="btn" disabled={monitor.isPending} onClick={() => monitor.mutate()}>
          {d.monitoring ? t('End Monitor') : t('Monitor')}
        </button>
      )}
      {perms.sticky && (
        <button className="btn" onClick={() => patch.mutate({ sticky: !issue.sticky })}>
          {issue.sticky ? t('Unstick') : t('Stick')}
        </button>
      )}
      <Link className="btn" to={`/report?clone=${issue.id}`}>
        {t('Clone')}
      </Link>
      {canClose && (
        <button className="btn" onClick={() => onStatus(90)}>
          {t('Close')}
        </button>
      )}
      {perms.move && (
        <button className="btn" onClick={() => setMoving(true)}>
          {t('Move')}
        </button>
      )}
      {perms.delete && (
        <button
          className="btn danger"
          disabled={del.isPending}
          onClick={() => confirm(t('Delete issue {id} permanently? This cannot be undone.', { id: pad7(issue.id) })) && del.mutate()}
        >
          {t('Delete')}
        </button>
      )}
      <ErrorBox error={patch.error || monitor.error || del.error} />
      {moving && (
        <MoveDialog
          d={d}
          projects={meta.projects.filter((p) => p.access >= th('move_bug') && p.id !== issue.project_id)}
          onClose={() => setMoving(false)}
          onMoved={(nd) => {
            setMoving(false);
            onChange(nd);
          }}
        />
      )}
    </div>
  );
}

function MoveDialog({ d, projects, onClose, onMoved }: { d: IssueDetail; projects: { id: number; name: string }[]; onClose: () => void; onMoved: (d: IssueDetail) => void }) {
  const [target, setTarget] = useState('');
  const move = useMutation({ mutationFn: () => api.patch<IssueDetail>(`/api/issues/${d.issue.id}`, { project_id: Number(target) }), onSuccess: onMoved });
  return (
    <Modal title={t('Move issue {id}', { id: pad7(d.issue.id) })} onClose={onClose} width={460}>
      <label className="field">
        <span className="field-label">{t('Move to project')}</span>
        <select value={target} onChange={(e) => setTarget(e.target.value)} autoFocus>
          <option value="">{t('Select a project…')}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <p className="subtle small">{t("If the category doesn't exist in the target project, it will be cleared.")}</p>
      <ErrorBox error={move.error} />
      <div className="form-footer">
        <span className="spacer" />
        <button className="btn subtle" onClick={onClose}>
          {t('Cancel')}
        </button>
        <button className="btn primary" disabled={!target || move.isPending} onClick={() => move.mutate()}>
          {t('Move Issues')}
        </button>
      </div>
    </Modal>
  );
}

function Reminder({ d, onDone, onCancel }: { d: IssueDetail; onDone: (d: IssueDetail) => void; onCancel: () => void }) {
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<(UserRef & { enabled: boolean })[]>('/api/users') });
  const [to, setTo] = useState<string[]>([]);
  const [text, setText] = useState('');
  const send = useMutation({
    mutationFn: () => api.post<IssueDetail>(`/api/issues/${d.issue.id}/reminder`, { user_ids: to.map(Number), text }),
    onSuccess: onDone,
  });
  return (
    <div className="inline-panel">
      <h3>{t('Send a Reminder')}</h3>
      <div className="reminder-grid">
        <label className="field">
          <span className="field-label">{t('To (Ctrl+click for several)')}</span>
          <select multiple size={8} value={to} onChange={(e) => setTo([...e.target.selectedOptions].map((o) => o.value))}>
            {users.data
              ?.filter((u) => u.enabled)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.realname ? `${u.realname} (${u.username})` : u.username}
                </option>
              ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('Reminder')}</span>
          <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Please have a look at this issue…')} />
        </label>
      </div>
      <p className="subtle small">{t('The recipients get an e-mail, and the reminder is recorded as a note on the issue.')}</p>
      <ErrorBox error={send.error} />
      <div className="form-footer">
        <button className="btn primary" disabled={!to.length || !text.trim() || send.isPending} onClick={() => send.mutate()}>
          {t('Send')}
        </button>
        <button className="btn subtle" onClick={onCancel}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  );
}

/** Mantis "change status" form: resolution / fixed in version / handler / note depending on target status. */
function StatusChange({ d, to, onDone, onCancel }: { d: IssueDetail; to: number; onDone: (d: IssueDetail) => void; onCancel: () => void }) {
  const { label, items } = useMeta();
  const { issue, perms } = d;
  const resolving = to >= 80 && issue.status < 80;
  const reopening = to < 80 && issue.status >= 80;
  const [resolution, setResolution] = useState(String(resolving ? 20 : issue.resolution));
  const [fixedIn, setFixedIn] = useState(issue.fixed_in_version);
  const [handler, setHandler] = useState(String(issue.handler_id ?? ''));
  const [duplicate, setDuplicate] = useState('');
  const [note, setNote] = useState('');
  const [noteFormat, setNoteFormat] = useState<TextFormat>('auto');
  const [priv, setPriv] = useState(false);
  const [time, setTime] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body: any = { status: to };
      if (to >= 80 && perms.update) {
        body.resolution = Number(resolution);
        body.fixed_in_version = fixedIn;
        if (Number(resolution) === 60 && duplicate) body.duplicate_id = Number(duplicate);
      }
      if (perms.assign && handler !== String(issue.handler_id ?? '')) body.handler_id = handler ? Number(handler) : null;
      if (note.trim()) body.note = { text: note, private: priv, time_tracking: parseTime(time), text_format: noteFormat };
      return api.patch<IssueDetail>(`/api/issues/${issue.id}`, body);
    },
    onSuccess: onDone,
  });
  const title = label('status', to).replace(/^./, (c) => c.toUpperCase());
  return (
    <Modal title={t('Change status of issue {id}', { id: pad7(issue.id) })} onClose={onCancel} width={620}>
      <div className="status-flow">
        <StatusBadge status={issue.status} /> <span className="subtle">→</span> <StatusBadge status={to} />
      </div>
      <div className="field-row">
        {to >= 80 && perms.update && (
          <>
            <label className="field">
              <span className="field-label">{t('Resolution')}</span>
              <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                {items('resolution').map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">{t('Fixed in Version')}</span>
              <select value={fixedIn} onChange={(e) => setFixedIn(e.target.value)}>
                <option value=""></option>
                {d.versions.map((v) => (
                  <option key={v.id} value={v.version}>
                    {v.version}
                  </option>
                ))}
              </select>
            </label>
            {Number(resolution) === 60 && (
              <label className="field">
                <span className="field-label">{t('Duplicate of issue #')}</span>
                <input value={duplicate} onChange={(e) => setDuplicate(e.target.value.replace(/\D/g, ''))} />
              </label>
            )}
          </>
        )}
        {perms.assign && (
          <label className="field">
            <span className="field-label">{t('Assigned To')}</span>
            <select value={handler} onChange={(e) => setHandler(e.target.value)}>
              <option value="">{t('[Nobody]')}</option>
              {d.handlers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.realname || u.username}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {reopening && <p className="subtle small">{t('The resolution will be set to "reopened".')}</p>}
      <NoteFields note={note} setNote={setNote} format={noteFormat} setFormat={setNoteFormat} priv={priv} setPriv={setPriv} time={time} setTime={setTime} perms={perms} />
      <ErrorBox error={save.error} />
      <div className="form-footer">
        <span className="spacer" />
        <button className="btn subtle" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {t('Set status: {status}', { status: title })}
        </button>
      </div>
    </Modal>
  );
}

function parseTime(s: string): number {
  const t = s.trim();
  if (!t) return 0;
  const m = /^(\d+):(\d{1,2})$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  const h = Number(t.replace(',', '.'));
  return Number.isFinite(h) ? Math.round(h * 60) : 0;
}

function NoteFields(props: {
  note: string;
  setNote: (s: string) => void;
  format: TextFormat;
  setFormat: (f: TextFormat) => void;
  priv: boolean;
  setPriv: (b: boolean) => void;
  time: string;
  setTime: (s: string) => void;
  perms: Record<string, boolean>;
  placeholder?: string;
  extra?: ReactNode;
}) {
  return (
    <div className="note-fields">
      <MentionTextarea
        rows={4}
        value={props.note}
        onChange={props.setNote}
        format={props.format}
        onFormatChange={props.setFormat}
        presets
        placeholder={props.placeholder ?? t('Add a note (optional)')}
      />
      <div className="note-options">
        {props.perms.private_note && (
          <label className="check">
            <input type="checkbox" checked={props.priv} onChange={(e) => props.setPriv(e.target.checked)} /> {t('Private')}
          </label>
        )}
        {props.perms.time_tracking && (
          <label className="check">
            {t('Time tracking (HH:MM)')} <input className="short" value={props.time} onChange={(e) => props.setTime(e.target.value)} placeholder="0:00" />
          </label>
        )}
        {props.extra}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Tags({ d, onChange }: { d: IssueDetail; onChange: (d: IssueDetail) => void }) {
  const { meta } = useMeta();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const done = (nd: IssueDetail) => {
    onChange(nd);
    qc.invalidateQueries({ queryKey: ['meta'] });
  };
  const add = useMutation({
    mutationFn: (names: string[]) => api.post<IssueDetail>(`/api/issues/${d.issue.id}/tags`, { names }),
    onSuccess: (nd) => {
      setText('');
      done(nd);
    },
  });
  const remove = useMutation({ mutationFn: (tagId: number) => api.del<IssueDetail>(`/api/issues/${d.issue.id}/tags/${tagId}`), onSuccess: done });
  const attached = new Set(d.tags.map((tag) => tag.id));
  return (
    <div>
      <div className="tags">
        {d.tags.map((tag) => (
          <span key={tag.id} className="tag">
            <Link to={`/issues?tag=${tag.id}&hide_status=0`}>{tag.name}</Link>
            {(d.perms.tag_detach || tag.user_id === meta.user.id) && (
              <button className="tag-x" title={t('Detach')} onClick={() => remove.mutate(tag.id)}>
                ×
              </button>
            )}
          </span>
        ))}
        {d.tags.length === 0 && <span className="subtle">{t('No tags attached.')}</span>}
      </div>
      {d.perms.tag_attach && (
        <form
          className="attach-tags"
          onSubmit={(e) => {
            e.preventDefault();
            const names = text.split(',').map((s) => s.trim()).filter(Boolean);
            if (names.length) add.mutate(names);
          }}
        >
          <span className="subtle small">{t('(Separate by ",")')}</span>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Attach tags')} />
          <select
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              setText(text ? `${text}, ${e.target.value}` : e.target.value);
            }}
          >
            <option value="">{t('Existing tags')}</option>
            {meta.tags
              .filter((tag) => !attached.has(tag.id))
              .map((tag) => (
                <option key={tag.id} value={tag.name}>
                  {tag.name}
                </option>
              ))}
          </select>
          <button className="btn" disabled={!text.trim() || add.isPending}>
            {t('Attach')}
          </button>
        </form>
      )}
      <ErrorBox error={add.error || remove.error} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function useMoveAttachment(onChange: (d: IssueDetail) => void) {
  return useMutation({
    mutationFn: ({ id, after_id }: { id: number; after_id: number | null }) => api.post<IssueDetail>(`/api/attachments/${id}/move`, { after_id }),
    onSuccess: onChange,
  });
}

function IssueFiles({ d, onChange }: { d: IssueDetail; onChange: (d: IssueDetail) => void }) {
  const { meta } = useMeta();
  const del = useMutation({ mutationFn: (id: number) => api.del<IssueDetail>(`/api/attachments/${id}`), onSuccess: onChange });
  const move = useMoveAttachment(onChange);
  const own = d.attachments.filter((a) => !a.note_id);
  if (own.length === 0) return null;
  return (
    <div className="tblock">
      <div className="dlabel">{t('Attached Files')}</div>
      <FileList
        files={own}
        canDelete={(a) => d.perms.delete_attachment || a.user_id === meta.user.id}
        onDelete={(a) => del.mutate(a.id)}
        onMove={d.perms.update ? (id, after_id) => move.mutate({ id, after_id }) : undefined}
      />
      <ErrorBox error={del.error || move.error} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function F({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function EditForm({ d, onSaved, onCancel }: { d: IssueDetail; onSaved: (d: IssueDetail) => void; onCancel: () => void }) {
  const { items, label } = useMeta();
  const { issue, perms } = d;
  const initial = {
    story_points: issue.story_points ?? '',
    estimated_time: effortInput(issue.estimated_minutes),
    needed_time: issue.needed_time_source === 'manual' ? effortInput(issue.needed_minutes) : '',
    summary: issue.summary,
    description: issue.description,
    steps_to_reproduce: issue.steps_to_reproduce,
    additional_information: issue.additional_information,
    category_id: issue.category_id == null ? '' : String(issue.category_id),
    priority: String(issue.priority),
    severity: String(issue.severity),
    reproducibility: String(issue.reproducibility),
    projection: String(issue.projection),
    eta: String(issue.eta),
    view_state: String(issue.view_state),
    status: String(issue.status),
    resolution: String(issue.resolution),
    handler_id: String(issue.handler_id ?? ''),
    platform: issue.platform,
    os: issue.os,
    os_build: issue.os_build,
    version: issue.version,
    target_version: issue.target_version,
    fixed_in_version: issue.fixed_in_version,
    build: issue.build,
    due_date: issue.due_date ? fmtDate(issue.due_date, false) : '',
  };
  const [f, setF] = useState(initial);
  const [cf, setCf] = useState<Record<string, string>>(Object.fromEntries(d.custom_fields.map((c) => [String(c.id), c.value ?? ''])));
  const [textFormat, setTextFormat] = useState<TextFormat>(issue.text_format ?? 'auto');
  const [note, setNote] = useState('');
  const [noteFormat, setNoteFormat] = useState<TextFormat>('auto');
  const [priv, setPriv] = useState(false);
  const [time, setTime] = useState('');
  const set = (k: keyof typeof initial) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      const numeric = new Set(['priority', 'severity', 'reproducibility', 'projection', 'eta', 'view_state', 'status', 'resolution']);
      for (const k of Object.keys(initial) as (keyof typeof initial)[]) {
        if (f[k] === initial[k]) continue;
        if (k === 'story_points' || k === 'estimated_time' || k === 'needed_time') continue;
        if (k === 'category_id' || k === 'handler_id') body[k] = f[k] === '' ? null : Number(f[k]);
        else if (k === 'due_date') body[k] = f[k] ? new Date(f[k] + 'T00:00:00').toISOString() : null;
        else if (numeric.has(k)) body[k] = Number(f[k]);
        else body[k] = f[k];
      }
      const planning = planningPayload(f, perms.time_tracking);
      if (f.story_points !== initial.story_points) body.story_points = planning.story_points;
      if (f.estimated_time !== initial.estimated_time) body.estimated_minutes = planning.estimated_minutes;
      if (perms.time_tracking && f.needed_time !== initial.needed_time) body.needed_minutes = planning.needed_minutes;
      const cfChanged = Object.fromEntries(Object.entries(cf).filter(([k, v]) => (d.custom_fields.find((c) => String(c.id) === k)?.value ?? '') !== v));
      if (Object.keys(cfChanged).length) body.custom_fields = cfChanged;
      if (textFormat !== (issue.text_format ?? 'auto')) body.text_format = textFormat;
      if (note.trim()) body.note = { text: note, private: priv, time_tracking: parseTime(time), text_format: noteFormat };
      return api.patch<IssueDetail>(`/api/issues/${issue.id}`, body);
    },
    onSuccess: onSaved,
  });

  const enumSelect = (k: keyof typeof initial, name: string) => (
    <select value={f[k]} onChange={set(k)}>
      {items(name).map((e) => (
        <option key={e.value} value={e.value}>
          {e.label}
        </option>
      ))}
    </select>
  );
  const versionSelect = (k: keyof typeof initial, disabled = false) => (
    <select value={f[k]} onChange={set(k)} disabled={disabled}>
      <option value=""></option>
      {d.versions
        .filter((v) => !v.obsolete || v.version === f[k])
        .map((v) => (
          <option key={v.id} value={v.version}>
            {v.version}
          </option>
        ))}
      {f[k] && !d.versions.some((v) => v.version === f[k]) && <option value={f[k]}>{f[k]}</option>}
    </select>
  );
  return (
    <form
      className="edit-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3>{t('Updating Issue Information')}</h3>
      <div className="field-row three">
        <F label={t('Category')}>
          <select value={f.category_id} onChange={set('category_id')}>
            <option value="">{t('(none)')}</option>
            {d.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.project_id == null ? `[${t('All Projects')}] ${c.name}` : c.name}
              </option>
            ))}
          </select>
        </F>
        {perms.change_view_state ? <F label={t('View Status')}>{enumSelect('view_state', 'view_state')}</F> : <span />}
        {perms.edit_target ? (
          <F label={t('Due Date')}>
            <input type="date" value={f.due_date} onChange={set('due_date')} />
          </F>
        ) : (
          <span />
        )}
        {perms.assign ? (
          <F label={t('Assigned To')}>
            <select value={f.handler_id} onChange={set('handler_id')}>
              <option value="">{t('[Nobody]')}</option>
              {d.handlers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.realname || u.username}
                </option>
              ))}
            </select>
          </F>
        ) : (
          <span />
        )}
        <F label={t('Priority')}>{enumSelect('priority', 'priority')}</F>
        <F label={t('Severity')}>{enumSelect('severity', 'severity')}</F>
        <F label={t('Reproducibility')}>{enumSelect('reproducibility', 'reproducibility')}</F>
        {perms.change_status ? (
          <F label={t('Status')}>
            <select value={f.status} onChange={set('status')}>
              {d.allowed_statuses.map((s) => (
                <option key={s} value={s}>
                  {items('status').find((x) => x.value === s)?.label}
                </option>
              ))}
            </select>
          </F>
        ) : (
          <span />
        )}
        <F label={t('Resolution')}>{enumSelect('resolution', 'resolution')}</F>
        <F label={t('Projection')}>{enumSelect('projection', 'projection')}</F>
        {/* Mantis' rough ETA is replaced by the estimated effort; shown only for issues that already have one */}
        {issue.eta !== 10 && <F label={t('ETA')}>{enumSelect('eta', 'eta')}</F>}
        <F label={t('Platform')}>
          <input value={f.platform} onChange={set('platform')} />
        </F>
        <F label={t('OS')}>
          <input value={f.os} onChange={set('os')} />
        </F>
        <F label={t('OS Version')}>
          <input value={f.os_build} onChange={set('os_build')} />
        </F>
        <F label={t('Product Build')}>
          <input value={f.build} onChange={set('build')} />
        </F>
        <F label={t('Product Version')}>{versionSelect('version')}</F>
        <F label={t('Target Version')}>{versionSelect('target_version', !perms.edit_target)}</F>
        <F label={t('Fixed in Version')}>{versionSelect('fixed_in_version')}</F>
      </div>
      <IssuePlanningFields value={f} onChange={(key, value) => setF({ ...f, [key]: value })} canTime={perms.time_tracking}
        autoMinutes={issue.needed_time_source === 'time_entries' ? issue.needed_minutes : undefined}
        span={statusSpan(d.history)} statusLabel={(s) => label('status', s)} />
      <F label={t('Summary')}>
        <input value={f.summary} onChange={set('summary')} maxLength={255} required />
      </F>
      {/* div, not label: a label would pass clicks on its text to the first toolbar button */}
      <div className="field">
        <span className="field-label">{t('Description')}</span>
        <MentionTextarea rows={8} value={f.description} onChange={(v) => setF({ ...f, description: v })} format={textFormat} onFormatChange={setTextFormat} />
      </div>
      <div className="field">
        <span className="field-label">{t('Steps To Reproduce')}</span>
        <MentionTextarea rows={4} value={f.steps_to_reproduce} onChange={(v) => setF({ ...f, steps_to_reproduce: v })} format={textFormat} />
      </div>
      <div className="field">
        <span className="field-label">{t('Additional Information')}</span>
        <MentionTextarea rows={3} value={f.additional_information} onChange={(v) => setF({ ...f, additional_information: v })} format={textFormat} />
      </div>
      {d.custom_fields.length > 0 && (
        <div className="field-row three">
          {d.custom_fields.map((c) => (
            <F key={c.id} label={c.name}>
              <CustomFieldInput field={c} value={cf[String(c.id)] ?? ''} onChange={(v) => setCf({ ...cf, [String(c.id)]: v })} disabled={!c.can_write} />
            </F>
          ))}
        </div>
      )}
      <NoteFields note={note} setNote={setNote} format={noteFormat} setFormat={setNoteFormat} priv={priv} setPriv={setPriv} time={time} setTime={setTime} perms={perms} />
      <ErrorBox error={save.error} />
      <div className="form-footer">
        <button className="btn primary" disabled={save.isPending}>
          {t('Update Information')}
        </button>
        <button type="button" className="btn subtle" onClick={onCancel}>
          {t('Cancel')}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
const icon = (d: string) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d={d} />
  </svg>
);

function Relationships({ d, onChange }: { d: IssueDetail; onChange: (d: IssueDetail) => void }) {
  const { label, items } = useMeta();
  const rowColor = useIssueRowColor();
  const [type, setType] = useState('1');
  const [target, setTarget] = useState('');
  const add = useMutation({
    mutationFn: () => api.post<IssueDetail>(`/api/issues/${d.issue.id}/relationships`, { type: Number(type), target_id: Number(target) }),
    onSuccess: (nd) => {
      setTarget('');
      onChange(nd);
    },
  });
  const del = useMutation({ mutationFn: (id: number) => api.del<IssueDetail>(`/api/relationships/${id}?from=${d.issue.id}`), onSuccess: onChange });
  if (d.relationships.length === 0 && !d.perms.relationships) return null;
  return (
    <Section title={t('Relationships')} icon={icon('M6 4v6h12V4M12 10v10M6 20h12')}>
      {d.perms.relationships && (
        <form
          className="rel-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (target) add.mutate();
          }}
        >
          <span>{t('Current issue')}</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {items('relationship').map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <input value={target} onChange={(e) => setTarget(e.target.value.replace(/\D/g, ''))} placeholder={t('Issue #')} className="short" />
          <button className="btn" disabled={!target || add.isPending}>
            {t('Add')}
          </button>
        </form>
      )}
      {d.relationships.length > 0 && (
        <table className="grid">
          <tbody>
            {d.relationships.map((r) => (
              <tr key={r.id} {...rowColor(r.issue.status)}>
                <td className="nowrap">{label('relationship', r.type)}</td>
                <td className="nowrap">
                  <Link to={`/issues/${r.issue.id}`} className="key">
                    {pad7(r.issue.id)}
                  </Link>
                </td>
                <td className="nowrap">
                  <StatusBadge status={r.issue.status} resolution={r.issue.resolution} />
                </td>
                <td className="nowrap">{r.issue.handler_name && <UserChip name={r.issue.handler_name} size={20} />}</td>
                <td className="nowrap subtle">{r.issue.project_name}</td>
                <td className="wide">{r.issue.summary}</td>
                <td className="narrow">
                  {d.perms.relationships && (
                    <button className="icon-btn danger" title={t('Delete relationship')} onClick={() => confirm(t('Delete this relationship?')) && del.mutate(r.id)}>
                      🗑
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ErrorBox error={add.error || del.error} />
    </Section>
  );
}

function Monitors({ d, onChange }: { d: IssueDetail; onChange: (d: IssueDetail) => void }) {
  const [username, setUsername] = useState('');
  const add = useMutation({
    mutationFn: () => api.post<IssueDetail>(`/api/issues/${d.issue.id}/monitors`, { username }),
    onSuccess: (nd) => {
      setUsername('');
      onChange(nd);
    },
  });
  const del = useMutation({ mutationFn: (uid: number) => api.del<IssueDetail>(`/api/issues/${d.issue.id}/monitors/${uid}`), onSuccess: onChange });
  return (
    <Section title={t('Users monitoring this issue')} icon={icon('M9 11a4 4 0 100-8 4 4 0 000 8zM17 11a3 3 0 100-6M2 21c0-3.5 3-6 7-6s7 2.5 7 6M17 15c3 0 5 2 5 5')}>
      <div className="monitor-row">
        <div className="tags">
          {d.monitors.map((u) => (
            <span key={u.id} className="tag user">
              <Avatar name={u.realname || u.username} size={18} /> {u.realname || u.username}
              {d.perms.monitor_others && (
                <button className="tag-x" onClick={() => del.mutate(u.id)} title={t('Remove')}>
                  ×
                </button>
              )}
            </span>
          ))}
          {d.monitors.length === 0 && <span className="subtle">{t('There are no users monitoring this issue.')}</span>}
        </div>
        {d.perms.monitor_others && (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (username.trim()) add.mutate();
            }}
          >
            <span className="subtle">{t('Username')}</span>
            <input className="short" value={username} onChange={(e) => setUsername(e.target.value)} />
            <button className="btn">{t('Add')}</button>
          </form>
        )}
      </div>
      <ErrorBox error={add.error || del.error} />
    </Section>
  );
}

// ---------------------------------------------------------------------------
function Activities({ d, onChange }: { d: IssueDetail; onChange: (d: IssueDetail) => void }) {
  const { meta } = useMeta();
  const desc = meta.prefs?.bugnote_order === 'DESC';
  const notes = desc ? [...d.notes].reverse() : d.notes;
  const moveNote = useMutation({
    mutationFn: ({ id, after_id }: { id: number; after_id: number | null }) => api.post<IssueDetail>(`/api/notes/${id}/move`, { after_id }),
    onSuccess: onChange,
  });
  // the server works in time order; with "newest first" the displayed order is reversed
  const noteDrag = useDragReorder(
    notes.map((n) => n.id),
    (id, order) => moveNote.mutate({ id, after_id: afterId(desc ? [...order].reverse() : order, id) }),
    d.perms.update,
  );
  const [note, setNote] = useState('');
  const [noteFormat, setNoteFormat] = useState<TextFormat>('auto');
  const [priv, setPriv] = useState(false);
  const [time, setTime] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const add = useMutation({
    mutationFn: async () => {
      let nd: IssueDetail;
      if (note.trim()) {
        nd = await api.post<IssueDetail>(`/api/issues/${d.issue.id}/notes`, { text: note, private: priv, time_tracking: parseTime(time), text_format: noteFormat });
      } else {
        nd = d;
      }
      if (files.length) {
        const fd = new FormData();
        for (const f of files) fd.append('file', f, f.name);
        // attach the files to the note just added (or to the issue when there's no text)
        const mine = note.trim() ? nd.notes.filter((n) => n.reporter_id === meta.user.id).sort((a, b) => b.id - a.id)[0] : undefined;
        nd = await api.post<IssueDetail>(`/api/issues/${d.issue.id}/attachments${mine ? `?note_id=${mine.id}` : ''}`, fd);
      }
      return nd;
    },
    onSuccess: (nd) => {
      setNote('');
      setPriv(false);
      setTime('');
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      onChange(nd);
    },
  });

  // paste screenshots anywhere on the page (outside text fields) → attach to the next note
  useEffect(() => {
    if (!d.perms.upload) return;
    const onPaste = (e: ClipboardEvent) => {
      const pasted = pastedFiles(e.clipboardData);
      if (!pasted.length) return;
      e.preventDefault();
      setFiles((cur) => [...cur, ...pasted]);
      document.getElementById('add-note')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [d.perms.upload]);

  const total = d.notes.reduce((s, n) => s + (n.time_tracking || 0), 0);
  const form = (d.perms.add_note || d.perms.upload) && (
    <form
      id="add-note"
      className={`add-note ${drag ? 'drag' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        setFiles([...files, ...e.dataTransfer.files]);
      }}
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim() || files.length) add.mutate();
      }}
    >
      <div className="add-note-head">
        <Avatar name={meta.user.realname || meta.user.username} size={32} />
        <strong>{t('Add Note')}</strong>
      </div>
      <NoteFields
        note={note}
        setNote={setNote}
        format={noteFormat}
        setFormat={setNoteFormat}
        priv={priv}
        setPriv={setPriv}
        time={time}
        setTime={setTime}
        perms={d.perms}
        placeholder={t('Add a note… (drop files here or paste a screenshot to attach them)')}
        extra={
          d.perms.upload && (
            <label className="btn subtle file-btn">
              📎 {t('Attach files')}
              <input ref={fileRef} type="file" multiple hidden onChange={(e) => setFiles([...files, ...(e.target.files ?? [])])} />
            </label>
          )
        }
      />
      <PendingFiles files={files} onChange={setFiles} />
      <ErrorBox error={add.error} />
      <div className="form-footer">
        <button className="btn primary" disabled={(!note.trim() && !files.length) || add.isPending}>
          {add.isPending ? t('Saving…') : t('Add Note')}
        </button>
      </div>
    </form>
  );
  return (
    <Section
      id="notes"
      title={t('Activities')}
      icon={icon('M4 5h16v10H8l-4 4z')}
      actions={d.perms.time_tracking && total > 0 ? <span className="subtle small">{t('Time tracking: {time}', { time: fmtMinutes(total) })}</span> : null}
    >
      {desc && form}
      {notes.length === 0 && <p className="subtle">{t('There are no notes attached to this issue.')}</p>}
      <div className="activities">
        <ErrorBox error={moveNote.error} />
        {notes.map((n) => (
          <NoteItem key={n.id} n={n} d={d} onChange={onChange} drag={noteDrag} />
        ))}
      </div>
      {!desc && form}
    </Section>
  );
}

function NoteItem({ n, d, onChange, drag }: { n: Note; d: IssueDetail; onChange: (d: IssueDetail) => void; drag: ReturnType<typeof useDragReorder> }) {
  const { meta } = useMeta();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(n.text);
  const [fmt, setFmt] = useState<TextFormat>(n.text_format ?? 'auto');
  const own = n.reporter_id === meta.user.id;
  const save = useMutation({
    mutationFn: (body: any) => api.patch<IssueDetail>(`/api/notes/${n.id}`, body),
    onSuccess: (nd) => {
      setEditing(false);
      onChange(nd);
    },
  });
  const del = useMutation({ mutationFn: () => api.del<IssueDetail>(`/api/notes/${n.id}`), onSuccess: onChange });
  const delFile = useMutation({ mutationFn: (id: number) => api.del<IssueDetail>(`/api/attachments/${id}`), onSuccess: onChange });
  const files = d.attachments.filter((a) => a.note_id === n.id);
  const moveFile = useMoveAttachment(onChange);
  const target = drag.target(n.id);
  const handle = drag.handle(n.id);
  return (
    <div
      {...target}
      className={`activity ${n.view_state === 50 ? 'private' : ''} ${n.note_type === 1 ? 'reminder' : ''} ${target.className}`}
      id={`c${n.id}`}
    >
      {/* the note is dragged by this column (avatar, name, date), so text in the note stays selectable */}
      <div {...handle} className={`activity-meta ${handle.className ?? ''}`} title={handle.draggable ? t('Drag to change the order') : undefined}>
        <Avatar name={n.realname || n.username} size={36} />
        <div>
          <strong>{n.realname || n.username}</strong>
          <div className="subtle small" title={fmtDate(n.created_at)}>
            🕓 {fmtDate(n.created_at)}
          </div>
          <div className="small">
            <a href={`#c${n.id}`} className="note-link">
              ~{pad7(n.id)}
            </a>
            {n.view_state === 50 && <span className="tag-mini private">{t('private')}</span>}
            {n.note_type === 1 && <span className="tag-mini">{t('reminder')}</span>}
          </div>
          {n.time_tracking > 0 && <div className="small">⏱ {fmtMinutes(n.time_tracking)}</div>}
          {n.updated_at !== n.created_at && <div className="subtle small">{t('edited {when}', { when: relTime(n.updated_at) })}</div>}
        </div>
      </div>
      <div className="activity-body">
        {editing ? (
          <>
            <MentionTextarea rows={5} value={text} onChange={setText} format={fmt} onFormatChange={setFmt} presets />
            <div className="form-footer">
              <button className="btn primary" disabled={!text.trim()} onClick={() => save.mutate({ text, text_format: fmt })}>
                {t('Save')}
              </button>
              <button className="btn subtle" onClick={() => setEditing(false)}>
                {t('Cancel')}
              </button>
            </div>
          </>
        ) : (
          <FormattedText text={n.text} format={n.text_format} />
        )}
        <FileList
          files={files}
          canDelete={(a) => d.perms.delete_attachment || a.user_id === meta.user.id}
          onDelete={(a) => delFile.mutate(a.id)}
          onMove={d.perms.update && !editing ? (id, after_id) => moveFile.mutate({ id, after_id }) : undefined}
        />
        {!editing && (own || d.perms.update_note_any || d.perms.delete_note_any || d.perms.private_note) && (
          <div className="activity-actions">
            {(own || d.perms.update_note_any) && (
              <button className="link small" onClick={() => setEditing(true)}>
                {t('Edit')}
              </button>
            )}
            {(own || d.perms.delete_note_any) && (
              <button className="link small danger" onClick={() => confirm(t('Delete this note?')) && del.mutate()}>
                {t('Delete')}
              </button>
            )}
            {d.perms.private_note && (
              <button className="link small" onClick={() => save.mutate({ private: n.view_state !== 50 })}>
                {n.view_state === 50 ? t('Make Public') : t('Make Private')}
              </button>
            )}
          </div>
        )}
        <ErrorBox error={save.error || del.error || delFile.error || moveFile.error} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const FIELD_LABELS: Record<string, string> = {
  story_points: N_('Complexity / Story points'),
  estimated_minutes: N_('Estimated time'),
  needed_minutes: N_('Needed time (actual)'),
  status: N_('Status'),
  handler_id: N_('Assigned To'),
  priority: N_('Priority'),
  severity: N_('Severity'),
  reproducibility: N_('Reproducibility'),
  resolution: N_('Resolution'),
  projection: N_('Projection'),
  eta: N_('ETA'),
  view_state: N_('View Status'),
  category: N_('Category'),
  project_id: N_('Project'),
  os: N_('OS'),
  os_build: N_('OS Version'),
  platform: N_('Platform'),
  version: N_('Product Version'),
  fixed_in_version: N_('Fixed in Version'),
  target_version: N_('Target Version'),
  build: N_('Product Build'),
  summary: N_('Summary'),
  due_date: N_('Due Date'),
  sticky: N_('Sticky Issue'),
  duplicate_id: N_('Duplicate ID'),
};
const ENUM_FIELDS: Record<string, string> = {
  status: 'status',
  priority: 'priority',
  severity: 'severity',
  reproducibility: 'reproducibility',
  resolution: 'resolution',
  projection: 'projection',
  eta: 'eta',
  view_state: 'view_state',
};

function History({ d }: { d: IssueDetail }) {
  const { label, project } = useMeta();
  const user = (v: string) => (v && v !== '0' ? (d.users[Number(v)] ?? `#${v}`) : '');

  const describe = (h: HistoryRow): [string, string] => {
    switch (h.type) {
      case 0: {
        const enumName = ENUM_FIELDS[h.field_name];
        const fmt = (v: string) => {
          if (h.field_name === 'story_points') return storyPointsText(v || null);
          if (h.field_name === 'estimated_minutes' || h.field_name === 'needed_minutes') return v === '' ? (h.field_name === 'needed_minutes' ? t('From time entries') : t('Not set')) : fmtMinutes(Number(v));
          if (enumName && /^\d+$/.test(v)) return label(enumName, Number(v));
          if (h.field_name === 'handler_id') return user(v);
          if (h.field_name === 'project_id') return project(Number(v))?.name ?? `#${v}`;
          if (h.field_name === 'due_date' && /^\d+$/.test(v)) return Number(v) > 1 ? fmtDate(new Date(Number(v) * 1000).toISOString(), false) : '';
          return v;
        };
        return [FIELD_LABELS[h.field_name] ? t(FIELD_LABELS[h.field_name]) : h.field_name, `${fmt(h.old_value)} => ${fmt(h.new_value)}`];
      }
      case 1:
        return [t('New Issue'), ''];
      case 2:
        return [t('Note Added: {v}', { v: pad7(h.old_value) }), ''];
      case 3:
        return [t('Note Edited: {v}', { v: pad7(h.old_value) }), ''];
      case 4:
        return [t('Note Deleted: {v}', { v: pad7(h.old_value) }), ''];
      case 6:
        return [t('Description Updated'), ''];
      case 7:
        return [t('Additional Information Updated'), ''];
      case 8:
        return [t('Steps to Reproduce Updated'), ''];
      case 9:
        return [t('File Added: {v}', { v: h.old_value }), ''];
      case 10:
        return [t('File Deleted: {v}', { v: h.old_value }), ''];
      case 11:
        return [t('Note View State: {v}', { v: pad7(h.new_value) }), label('view_state', Number(h.old_value))];
      case 12:
        return [t('Issue Monitored: {v}', { v: user(h.old_value) }), ''];
      case 13:
        return [t('Issue End Monitor: {v}', { v: user(h.old_value) }), ''];
      case 18:
        return [t('Relationship added'), `${label('relationship', Number(h.old_value))} ${pad7(h.new_value)}`];
      case 19:
        return [t('Relationship deleted'), `${label('relationship', Number(h.old_value))} ${pad7(h.new_value)}`];
      case 20:
        return [t('Issue cloned'), h.new_value];
      case 21:
        return [t('Issue generated from'), h.new_value];
      case 25:
        return [t('Tag Attached: {v}', { v: h.old_value }), ''];
      case 26:
        return [t('Tag Detached: {v}', { v: h.old_value }), ''];
      default:
        return [h.field_name || t('change ({type})', { type: h.type }), h.old_value && h.new_value ? `${h.old_value} => ${h.new_value}` : h.old_value || h.new_value];
    }
  };

  return (
    <Section id="history" title={t('Issue History')} icon={icon('M3 12a9 9 0 103-6.7L3 8M3 3v5h5M12 7v5l3 3')}>
      <div className="table-wrap">
        <table className="grid history">
          <thead>
            <tr>
              <th>{t('Date Modified')}</th>
              <th>{t('Username')}</th>
              <th>{t('Field')}</th>
              <th>{t('Change')}</th>
            </tr>
          </thead>
          <tbody>
            {d.history.map((h) => {
              const [field, change] = describe(h);
              return (
                <tr key={h.id}>
                  <td className="nowrap">{fmtDate(h.created_at)}</td>
                  <td className="nowrap">{h.username}</td>
                  <td>{field}</td>
                  <td>{change}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
